import React from "react";
import { motion } from "framer-motion";

const CHAPTERS = [
    {
        num: "01",
        title: "Blowing",
        machine: "PET Blowing Machine",
        body: "Preforms are heated with pinpoint infrared precision, then blown at high pressure into our signature Victory bottle silhouette — a shape engineered for grip, gloss and shelf presence.",
    },
    {
        num: "02",
        title: "RFC Filling",
        machine: "Rinse · Fill · Cap Monobloc",
        body: "Each bottle is triple-rinsed with purified water, filled in a stainless-steel isobaric chamber, and capped instantly — untouched by air, humidity, or hand. Purity, sealed at speed.",
    },
    {
        num: "03",
        title: "Label",
        machine: "Label",
        body: "Our label is applied wet-free, then tunnel-shrunk in seconds — hugging every curve of the bottle. This is where the Victory bubble mark meets the glassy skin of the product.",
    },
    {
        num: "04",
        title: "Packing",
        machine: "Auto Shrink-Wrap & Case Packer",
        body: "Batches are grouped, shrink-wrapped, and stacked onto palletized cases — ready for the cold-chain journey. Every case is traceable, every bottle accounted for.",
    },
];

const Manifesto = () => {
    return (
        <section
            id="process"
            className="section-ivory cv-auto relative z-10 py-28 md:py-40"
            data-testid="manifesto-section"
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-12">
                {/* Section label */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="relative flex items-baseline justify-between pt-6"
                >
                    <div className="divider-gold absolute inset-x-0 top-0" />
                    <span className="text-xs uppercase tracking-[0.4em] text-crimson-800/80">
                        <span className="text-gold">§</span> The Manifesto
                    </span>
                    <span className="text-xs uppercase tracking-[0.4em] text-crimson-800/60">
                        Four Movements
                    </span>
                </motion.div>

                {/* Big claim */}
                <motion.h2
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{
                        duration: 1.1,
                        ease: [0.22, 1, 0.36, 1],
                        delay: 0.1,
                    }}
                    className="font-serif-display mt-10 text-[10vw] leading-[0.92] text-crimson-800 md:text-[7vw]"
                >
                    Bottled, not
                    <br />
                    <em className="italic text-crimson-800/80">manufactured.</em>
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.35 }}
                    className="mt-8 max-w-xl text-base leading-relaxed text-ink-800/80 md:ml-auto md:text-right"
                >
                    Four movements from a single line — each machine tuned like
                    an instrument. This is the choreography behind every bottle
                    that leaves the Victory plant.
                </motion.p>

                {/* Chapters */}
                <div className="mt-24 md:mt-32">
                    {CHAPTERS.map((c, idx) => (
                        <ChapterRow key={c.num} chapter={c} index={idx} />
                    ))}
                </div>
            </div>
        </section>
    );
};

const ChapterRow = ({ chapter, index }) => {
    return (
        <motion.article
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{
                duration: 1,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.05 * index,
            }}
            className="group relative grid grid-cols-12 gap-8 py-16 md:py-24"
            data-testid={`chapter-${chapter.num}`}
        >
            {/* Gold hairline divider */}
            <div className="divider-gold absolute inset-x-0 top-0" />

            {/* Left: huge number */}
            <div className="col-span-12 md:col-span-4 md:col-start-1">
                <div className="flex items-start gap-4">
                    <span className="mt-3 text-xs uppercase tracking-[0.4em] text-crimson-800/60">
                        <span className="text-gold">§</span> {String(index + 1).padStart(2, "0")}
                    </span>
                    <motion.span
                        initial={{ y: 20, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{
                            duration: 1.1,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                        className="font-serif-display text-[26vw] leading-[0.75] text-crimson-800 md:text-[14vw]"
                    >
                        {chapter.num}
                    </motion.span>
                </div>
            </div>

            {/* Right: content */}
            <div className="col-span-12 md:col-span-7 md:col-start-6 flex flex-col justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-crimson-800/70">
                        {chapter.machine}
                    </p>
                    <h3 className="font-serif-display mt-4 text-5xl leading-[1] text-ink-900 md:text-7xl">
                        {chapter.title}.
                    </h3>
                </div>
                <p className="mt-8 max-w-xl text-base leading-[1.7] text-ink-800/85 md:text-lg">
                    {chapter.body}
                </p>
            </div>
        </motion.article>
    );
};

export default Manifesto;
